// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — WebSocket Hook
// ═══════════════════════════════════════════════════════════

import { useEffect, useRef, useCallback, useState } from 'react';
import { store } from './store';

interface WsMessage {
  type: string;
  channel?: string;
  data?: unknown;
  [key: string]: unknown;
}

export function useWebSocket(onMessage?: (msg: WsMessage) => void) {
  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsUrl = `${protocol}://${window.location.host}/ws`;

    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          setConnected(true);
          store.wsConnected = true;
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data) as WsMessage;
            onMessage?.(msg);
          } catch {
            // ignore
          }
        };

        ws.onclose = () => {
          setConnected(false);
          store.wsConnected = false;
          // Reconnect after 3 seconds
          setTimeout(connect, 3000);
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch {
        setTimeout(connect, 3000);
      }
    };

    connect();

    return () => {
      wsRef.current?.close();
    };
  }, [onMessage]);

  const subscribe = useCallback((channel: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', channel }));
    }
  }, []);

  const unsubscribe = useCallback((channel: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe', channel }));
    }
  }, []);

  return { connected, subscribe, unsubscribe };
}
